import React, { useState, useEffect } from 'react';
import { useAudio } from '../../contexts/AudioContext';
import { MediumClip } from '../../components/MediumClip/MediumClip';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { QueueHeader } from './QueueHeader';
import { EXTENDED_HEADER_HEIGHT, MOBILE_MEDIUM_HEADER_HEIGHT, PLAYBACK_BAR_HEIGHT, MOBILE_PLAYBACK_BAR_HEIGHT } from '../../constants';
import { pixelsToVh } from '../../helpers/PixelsToViewport';
import { useNavigate } from 'react-router-dom';
import "./Queue.css"; // Make sure to import your CSS file

function DraggableQueue() {
    const navigate = useNavigate();
    const { currentlyPlayingClip, queue, moveClipInQueue, removeFromQueue, addToQueueAtIndex, autoqueue, moveClipInAutoqueue, removeFromAutoqueue, addToAutoqueueAtIndex } = useAudio();
    const [isAllMinimized, setIsAllMinimized] = useState(true);
    const windowHeight = window.innerHeight;
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleToggleAllMinimize = () => {
        setIsAllMinimized(!isAllMinimized);
    };

    const handleNavigateToFeed = () => {
        navigate('/feed');
    };


    function onDragEnd(result) {
        const { source, destination } = result;

        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            if (source.droppableId === 'queue') {
                moveClipInQueue(source.index, destination.index);
            } else if (source.droppableId === 'autoqueue') {
                moveClipInAutoqueue(source.index, destination.index);
            }
        } else {
            // Moving from autoqueue to queue
            if (source.droppableId === 'autoqueue' && destination.droppableId === 'queue') {
                const clipToMove = autoqueue[source.index];
                removeFromAutoqueue(source.index);
                addToQueueAtIndex(clipToMove, destination.index);
            } else if (source.droppableId === 'queue' && destination.droppableId === 'autoqueue') {
                const clipToMove = queue[source.index];
                removeFromQueue(source.index);
                addToAutoqueueAtIndex(clipToMove, destination.index);
            }
        }
    }


    return (
        <div>
            <QueueHeader handleToggleAllMinimize={handleToggleAllMinimize} isAllMinimized={isAllMinimized} />
            <div className="queueContentContainer" style={{ height: `${(100 - pixelsToVh(windowWidth > 768 ? EXTENDED_HEADER_HEIGHT + PLAYBACK_BAR_HEIGHT : MOBILE_MEDIUM_HEADER_HEIGHT + MOBILE_PLAYBACK_BAR_HEIGHT, windowHeight))}dvh` }}>
                {currentlyPlayingClip &&
                    <>
                        <p className="queueHeader">Now Playing</p>
                        <MediumClip clip={currentlyPlayingClip} isGloballyMinimized={isAllMinimized} color={'#D9D9D9'} />
                        <hr className='horizontalLine' />
                    </>
                }
                <DragDropContext onDragEnd={onDragEnd}>
                    <StrictModeDroppable droppableId="queue">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="queue-container"
                            >
                                {<p className="queueHeader">Your Queue</p>}
                                {queue.length === 0 &&
                                    <div className="emptyQueueMessage">{"Queue is empty. "}
                                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={handleNavigateToFeed}>Keep browsing.</span>
                                    </div>}
                                {queue.map((clip, index) => (
                                    <Draggable draggableId={clip.id} index={index} key={clip.id}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="draggable-item"
                                            >
                                                <MediumClip clip={clip} isGloballyMinimized={isAllMinimized} />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </StrictModeDroppable>
                    <hr className='horizontalLine' />
                    <StrictModeDroppable droppableId="autoqueue">
                        {(provided, snapshot) => (
                            <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                className="queue-container"
                            >
                                {autoqueue.length > 0 && <p className="queueHeader">Up Next</p>}
                                {autoqueue.map((clip, index) => (
                                    <Draggable draggableId={clip.id} index={index} key={clip.id}>
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                className="draggable-item"
                                            >
                                                <MediumClip clip={clip} isGloballyMinimized={isAllMinimized} isInAutoqueue={true} />
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </div>
                        )}
                    </StrictModeDroppable>
                </DragDropContext>
            </div>
        </div>
    );
}

export default DraggableQueue;

const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true));

        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, []);

    if (!enabled) {
        return null;
    }

    return <Droppable {...props}>{children}</Droppable>;
}